import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "./Invoice.css";
import Logo from '../Assets/pdf-header.jpg';

const Invoice = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const order_id = params.get("order_id");
  const [orderData, setOrderData] = useState(null);
  const [settingsData, setSettingsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_ADMIN_API_BASE_URL;
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (!order_id) {
      setError("No order ID provided in the URL.");
      setLoading(false);
      return;
    }

    const fetchSettingsDetails = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}setting_detail.php`);
        if (response.data && response.data.head && response.data.head.code === 200) {
          setSettingsData(response.data.body);
        }
      } catch (error) {
        console.error("Error fetching settings details:", error);
      }
    };

    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.post(
          `${API_BASE_URL}order_details.php`,
          { order_id: String(order_id) },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data && response.data.head && response.data.head.code === 200) {
          setOrderData(response.data.body);
        } else {
          setError("Failed to fetch order details.");
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
        setError("Failed to load order details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
    fetchSettingsDetails();
  }, [order_id, API_BASE_URL]);

  useEffect(() => {
    if (orderData && imageLoaded) {
      setTimeout(() => {
        window.print();
      }, 500);
    }
  }, [orderData, imageLoaded]);

  // Loading State
  if (loading) {
    return (
      <div className="invoice-loading">
        <div className="invoice-loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>Loading invoice...</p>
        <p className="invoice-loading-id">Order ID: {order_id || 'Not provided'}</p>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="invoice-error">
        <div className="invoice-error-icon">⚠️</div>
        <h3>Unable to Load Invoice</h3>
        <p>{error}</p>
        <p className="invoice-error-order">Order ID: {order_id}</p>
        <button 
          className="invoice-error-btn"
          onClick={() => window.close()}
        >
          Close Window
        </button>
        <button 
          className="invoice-error-btn"
          onClick={() => window.location.reload()}
          style={{ marginLeft: '10px', background: '#28a745' }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="invoice-error">
        <div className="invoice-error-icon">📄</div>
        <h3>No Order Data Found</h3>
        <p>Unable to retrieve order details for Order ID: {order_id}</p>
        <button 
          className="invoice-error-btn"
          onClick={() => window.close()}
        >
          Close Window
        </button>
      </div>
    );
  }

  // Calculate values like OrderDetails page
  const subtotal = parseFloat(orderData.subtotal) || 0;
  const discountPercentage = parseFloat(orderData.discount_percentage) || 0;
  const discountAmount = parseFloat(orderData.discount_amt) || 0;
  const orderTotal = subtotal - discountAmount;
  const packingPercentage = parseFloat(orderData.packing_percentage) || 0;
  const packingAmount = parseFloat(orderData.packing_amt) || 0;
  const grandTotalRaw = parseFloat(orderData.total) || 0;
  const grandTotal = Math.round(grandTotalRaw);

  return (
    <>
      <div className="watermark">vasukitraders</div>
      <div className="invoice-wrapper">
        <div className="invoice-container">
          <table className="invoice-table">
            <tbody>
              {/* Header with Logo */}
              <tr>
                <td className="invoice-header">
                  <img
                    src={Logo}
                    alt="Company Logo"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(true)}
                    style={{ width: "100%", height: "auto" }}
                  />
                </td>
              </tr>

              {/* Order Info */}
              <tr>
                <td className="order-info">
                  <div className="order-row-flex">
                    <span><strong>Order ID:</strong> {orderData.order_id}</span>
                    <span><strong>Date:</strong> {orderData.orderdate}</span>
                    {/* <span><strong>Phone:</strong> {settingsData?.phone || orderData.mobile || 'N/A'}</span> */}
                  </div>
                </td>
              </tr>

              {/* Billing Information */}
              <tr>
                <td className="billing-shipping">
                  <div className="billing">
                    <h3>Billing Information</h3>
                    <p><strong>{orderData.billing_name}</strong></p>
                    <p>
                      {orderData.billing_address}, {orderData.billing_city},{" "}
                      {orderData.billing_state}, {orderData.billing_pincode}
                    </p>
                    <p><strong>Mobile:</strong> {orderData.mobile}</p>
                    <p><strong>Email:</strong> {orderData.email}</p>
                    {orderData.coupen_code && (
                      <p><strong>Referral ID:</strong> {orderData.coupen_code}</p>
                    )}
                  </div>
                </td>
              </tr>

              {/* Product Table */}
              <tr>
                <td className="products-section">
                  <table className="product-table-invoice">
                    <thead>
                      <tr>
                        <th className="col-sno">S.No</th>
                        <th className="col-product">Product Name</th>
                        <th className="col-qty">Qty</th>
                        <th className="col-price">Price (₹)</th>
                        {/* <th className="col-discount">Discount (₹)</th> */}
                        <th className="col-total">Total (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderData.order_details && orderData.order_details.map((item, index) => {
                        const price = parseFloat(item.amount) || 0;
                        const qty = parseFloat(item.qty) || 0;
                        const discountPercent = discountPercentage;
                        
                        // Price column: price × qty (total before discount)
                        const priceXQty = price * qty;
                        
                        // Discount amount per product line
                        const discountPerUnit = (price * discountPercent) / 100;
                        const totalDiscountForLine = discountPerUnit * qty;
                        
                        // Total after discount for this line
                        const totalAfterDiscount = priceXQty - totalDiscountForLine;
                        
                        return (
                          <tr key={index}>
                            <td className="col-sno">{index + 1}</td>
                            <td className="col-product">{item.product_name}</td>
                            <td className="col-qty">{qty}</td>
                            <td className="col-price">₹ {price.toFixed(2)}</td>
                            {/* <td className="col-discount" style={{ color: '#dc3545' }}>- ₹ {totalDiscountForLine.toFixed(2)}</td> */}
                            <td className="col-total"><b>₹ {totalAfterDiscount.toFixed(2)}</b></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </td>
              </tr>

              {/* Totals Section */}
              <tr>
                <td className="totals-section">
                  <table className="totals-table">
                    <tbody>
                      {/* Order Total after discount */}
                      <tr>
                        <td align="right" style={{ fontWeight: "bold", borderTop: "1px dashed #ddd", padding: "8px" }}>Order Total:</td>
                        <td align="right" style={{ borderTop: "1px dashed #ddd", padding: "8px" }}>₹ {orderTotal.toFixed(2)}</td>
                      </tr>

                      {/* Packing Charge */}
                      {packingAmount > 0 && (
                        <tr>
                          <td align="right" style={{ fontWeight: "bold", padding: "8px" }}>Packing Charge ({packingPercentage}%):</td>
                          <td align="right" style={{ padding: "8px" }}>+ ₹ {packingAmount.toFixed(2)}</td>
                        </tr>
                      )}

                      {/* Grand Total */}
                      <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                        <td align="right" style={{ fontSize: "16px", padding: "10px 8px" }}>Grand Total:</td>
                        <td align="right" style={{ fontSize: "16px", color: "#28a745", padding: "10px 8px" }}>₹ {grandTotal}</td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>

              {/* Footer */}
              <tr>
                <td className="footer-message">
                  <p className="diwali-wish">Thank you for shopping with us!</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Invoice;